import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/pro-solid-svg-icons/faPrint';
import { faEdit } from '@fortawesome/pro-solid-svg-icons/faEdit';
import { faWeight } from '@fortawesome/pro-solid-svg-icons/faWeight';
import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { NotificationService, NOTIFICATION_TYPE_SUCCESS, NOTIFICATION_DURATIONS } from 'ui-components/services/notification-service';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { GuidService } from 'ui-components/services/guid-service';

import { shipstationConnect } from '../../../services/shipstation-connect';
import { CONNECT_RESPONSE_SCALE_READ } from '../../../services/pusher-service/pusher-contract-messages';
import { usePusherEvent } from '../../../hooks/use-pusher-event';
import { savePrintSettings } from '../../../store/print-settings/print-settings.actions';
import Global from '../../../store/global-selectors';

import { updateConfigByDeviceType } from '../api/ss-connect';
import { IntlUnitFormattingService } from '../../../services/intl-unit-formatting-services/intl-unit-formatting-service';

import style from './style.module.less';
import messages from './messages';
import { PRINTER, SCALE, DISABLED_CHECKBOX_NAME, SHARED_CHECKBOX_NAME, DIMENSIONALIZER } from './constants';
import { DevicePropType } from './prop-types';
import { convertConnectWeightToOunces } from '../../../services/shipstation-connect/connect-scales';
import { emptyArray } from 'ui-components/default-props/empty';

const updateDevice = (device, field, value) => {
  return field === DISABLED_CHECKBOX_NAME ? { ...device, [field]: value, [SHARED_CHECKBOX_NAME]: false } : { ...device, [field]: value };
};

const ConnectedDevice = ({
  deviceType,
  device,
  openModal,
  userId,
  hasAdminOrConfigPermissions = false,
  workstationId,
  currentUserWorkstationIds = emptyArray,
}) => {
  const sellerDefaultDimensionsUnit = useSelector(Global.selectSellerDefaultDimensionUnit);
  const sellerDefaultWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);

  const [lastNotificationId, setNotificationId] = useState();
  const dispatch = useDispatch();
  const printSettings = useSelector(Global.selectPrintSettingsPrintSettings);

  const onReadScaleResponse = useCallback(
    (message) => {
      const { lbs, oz } = message?.weight || {};
      const weightAsOz = {
        unit: 'Ounces',
        value: convertConnectWeightToOunces(lbs, oz),
      };
      const dimensions = { ...message.dims, unit: 'inches' };

      const notificationOptions = {
        dontHide: false,
        duration: NOTIFICATION_DURATIONS.SHORT,
        title: <FormattedMessage {...messages.readScaleSuccess} />,
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        content: IntlUnitFormattingService.formatWeight(weightAsOz, sellerDefaultWeightUnit),
      };

      if (message.dims) {
        notificationOptions.title = <FormattedMessage {...messages.readDimensionalizerSuccess} />;
        notificationOptions.content = (
          <>
            <div>{IntlUnitFormattingService.formatWeight(weightAsOz, sellerDefaultWeightUnit)}</div>
            <div className={style['dims-message']}>
              {IntlUnitFormattingService.formatDimensions(dimensions, sellerDefaultDimensionsUnit)}
            </div>
          </>
        );
      }

      NotificationService.updateNotification(lastNotificationId, notificationOptions);
      setNotificationId(null);
    },
    [lastNotificationId, sellerDefaultDimensionsUnit, sellerDefaultWeightUnit],
  );

  usePusherEvent(CONNECT_RESPONSE_SCALE_READ, onReadScaleResponse);

  const editDevice = useCallback(() => {
    openModal(device, deviceType);
  }, [device, deviceType, openModal]);

  const onCheckboxChange = useCallback(
    (checked, e) => {
      const { name } = e.target;
      const handler = updateConfigByDeviceType[deviceType];

      handler({
        workstationId: device.workstationId,
        device: updateDevice(device, name, checked),
        userId,
      });
      if (!checked && deviceType === PRINTER && name === DISABLED_CHECKBOX_NAME) {
        // When a printer is disabled we want to reset any print settings currently using it to always prompt
        Object.keys(printSettings).forEach((k) => {
          if (printSettings[k].id === device.id) {
            dispatch(savePrintSettings(k, { name: 'always prompt' }));
          }
        });
      }
    },
    [deviceType, device, userId, dispatch, printSettings],
  );

  const readScale = useCallback(() => {
    const requestId = GuidService.newGuid();
    const notificationId = NotificationService.showNotification({
      dontHide: true,
      title: <FormattedMessage {...messages.readingFromScale} />,
      notificationType: 'info',
      content: <LoadingIndicator />,
    });
    setNotificationId(notificationId);
    shipstationConnect.readFromScale(device, requestId);
  }, [device, setNotificationId]);

  const isUserWorkstation = currentUserWorkstationIds.find((wsId) => wsId === workstationId);
  const shouldShowAdminConfigureSettings = hasAdminOrConfigPermissions || isUserWorkstation;

  return (
    <li key={device.id} className={style.row}>
      <span className={style.name}>
        <FontAwesomeIcon className={style['item-icon']} icon={faPrint} />
        {device.name}
        <Button className={style.edit} onClick={editDevice} theme='link'>
          <FormattedMessage {...messages.edit} />
          <FontAwesomeIcon icon={faEdit} />
        </Button>
        {(deviceType === SCALE || deviceType === DIMENSIONALIZER) && (
          <Button className={style.edit} onClick={readScale} theme='link'>
            <FormattedMessage {...messages.readFromScaleTitle} />
            <FontAwesomeIcon icon={faWeight} />
          </Button>
        )}
      </span>
      {shouldShowAdminConfigureSettings && (
        <>
          <Checkbox
            checked={device.disabled}
            className={style['checkbox-column-items']}
            name={DISABLED_CHECKBOX_NAME}
            onChange={onCheckboxChange}
          />
          <Checkbox
            checked={device.shared}
            className={style['checkbox-column-items']}
            disabled={device.disabled}
            name={SHARED_CHECKBOX_NAME}
            onChange={onCheckboxChange}
          />
        </>
      )}
    </li>
  );
};

ConnectedDevice.propTypes = {
  deviceType: PropTypes.oneOf([PRINTER, SCALE, DIMENSIONALIZER]).isRequired,
  device: DevicePropType.isRequired,
  hasAdminOrConfigPermissions: PropTypes.bool,
  openModal: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
  workstationId: PropTypes.string.isRequired,
  currentUserWorkstationIds: PropTypes.arrayOf(PropTypes.string),
};

export default ConnectedDevice;
